import { Block, Flex, ProductColor, Tooltip, Typography } from '@aloft/toolkit-sui';

import { useRef } from 'react';

import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridCellKeyId } from '../types.ts';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import {
  alertTargetFieldHoverSx,
  getAlertEmphasisBoxShadow,
  getAlertTargetInputFieldSx,
  readOnlyFieldTypographyProps,
} from './styles.ts';

type AlertTargetReadOnlyCellProps = {
  children: string;
  cellKeyId: PropertyGridCellKeyId;
  gridColumn: string;
  propertyId: string;
  color?: ProductColor;
};

export const AlertTargetReadOnlyCell = ({
  children,
  cellKeyId,
  gridColumn,
  propertyId,
  color,
}: AlertTargetReadOnlyCellProps) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const { isAlertHighlighted, isAlertStyled, alertStyleVariant, alertCellTooltip } =
    usePropertyGridCellFocus({
      cellKeyId,
      propertyId,
      fieldRef: cellRef,
    });

  const alertTargetSx = getAlertTargetInputFieldSx(
    isAlertStyled,
    alertStyleVariant,
    isAlertHighlighted,
  );
  const alertEmphasisBoxShadow = getAlertEmphasisBoxShadow(alertStyleVariant);
  const showAlertEmphasis = Boolean(alertTargetSx && isAlertHighlighted);
  const showAlertTooltip = Boolean(alertTargetSx && alertCellTooltip);

  const cellSx = {
    boxSizing: 'border-box' as const,
    width: '100%',
    minHeight: PROPERTY_GRID_ROW_HEIGHT,
    padding: '4px 8px',
    border: 'none',
    backgroundColor:
      alertTargetSx?.backgroundColor ??
      readOnlyFieldTypographyProps.__dangerDoNotUseSx?.backgroundColor,
    ...(showAlertEmphasis
      ? {
          filter: alertTargetFieldHoverSx.filter,
          boxShadow: alertEmphasisBoxShadow,
        }
      : {}),
    _hover: showAlertEmphasis
      ? {
          backgroundColor: alertTargetSx?.backgroundColor,
          filter: alertTargetFieldHoverSx.filter,
          boxShadow: alertEmphasisBoxShadow,
        }
      : undefined,
  };

  const cell = (
    <Flex
      ref={cellRef}
      width="100%"
      alignItems="center"
      justifyContent="flex-end"
      __dangerDoNotUseSx={cellSx}
    >
      <Typography
        {...readOnlyFieldTypographyProps}
        color={color}
        textAlign="right"
        __dangerDoNotUseSx={{
          ...readOnlyFieldTypographyProps.__dangerDoNotUseSx,
          width: '100%',
          height: 'auto',
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
        }}
      >
        {children}
      </Typography>
    </Flex>
  );

  if (!showAlertTooltip) {
    return (
      <Block width="100%" __dangerDoNotUseSx={{ gridColumn }}>
        {cell}
      </Block>
    );
  }

  return (
    <Block width="100%" __dangerDoNotUseSx={{ gridColumn }}>
      <Tooltip label={alertCellTooltip} placement="top" hasArrow>
        <Block width="100%" height={PROPERTY_GRID_ROW_HEIGHT}>
          {cell}
        </Block>
      </Tooltip>
    </Block>
  );
};
